<template>
  <div>
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-semibold text-gray-900">{{ title }}</h3>
    </div>

    <!-- Status -->
    <div class="mb-4">
      <label class="block text-xs font-medium text-gray-500 uppercase mb-1.5">Status</label>
      <slot name="status-select"></slot>
    </div>

    <!-- Attempts -->
    <div class="mb-4">
      <label class="block text-xs font-medium text-gray-500 uppercase mb-1.5">Attempts</label>
      <div class="flex items-center gap-3">
        <button
          @click="updateAttempts(localAttempts - 1)"
          class="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-50"
          :disabled="localAttempts <= 0 || isSaving"
        >
          <Minus class="w-3 h-3" />
        </button>
        <span class="text-sm font-medium w-8 text-center">{{ localAttempts }}</span>
        <button
          @click="updateAttempts(localAttempts + 1)"
          class="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-50"
          :disabled="isSaving"
        >
          <Plus class="w-3 h-3" />
        </button>
      </div>
    </div>

    <!-- Notes -->
    <div class="mb-4">
      <label class="block text-xs font-medium text-gray-500 uppercase mb-1.5">Notes</label>
      <textarea
        v-model="localNotes"
        rows="3"
        class="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
        placeholder="Add a note..."
        :disabled="isSaving"
      ></textarea>
    </div>

    <!-- Actions -->
    <div class="flex justify-end gap-2">
      <button
        @click="handleSave"
        class="px-3 py-1.5 text-xs font-medium bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5"
        :disabled="isSaving || !hasChanges"
      >
        <Loader2 v-if="isSaving" class="animate-spin w-3 h-3" />
        Save
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Plus, Minus, Loader2 } from 'lucide-vue-next'

const props = defineProps<{
  title: string
  attempts: number
  notes: string
  isSaving?: boolean
}>()

const emit = defineEmits<{
  (e: 'save', data: { attempts: number; notes: string }): void
}>()

const localAttempts = ref(props.attempts || 0)
const localNotes = ref(props.notes || '')

const hasChanges = computed(() => {
  return localAttempts.value !== props.attempts || localNotes.value !== props.notes
})

const updateAttempts = (val: number) => {
  if (val < 0) return
  localAttempts.value = val
}

const handleSave = () => {
  emit('save', {
    attempts: localAttempts.value,
    notes: localNotes.value
  })
}

// Sync props if they change externally
watch(
  () => props.attempts,
  (val) => (localAttempts.value = val)
)
watch(
  () => props.notes,
  (val) => (localNotes.value = val)
)
</script>
